import { KYCFileType, SubjectAction } from '@dealstack/shared/lib/generated';

import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { INVESTOR_TEST_ELEMENT_ID as TEST_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Button, Flex, Text } from '@mantine/core';
import { LastVerifiedBadge } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/LastVerifiedBadge';
import { isKycRejected } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import styled from 'styled-components';
import { KycInstructionBar } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/KycInstructions/KycInstructionBar';
import { ProofFileBox } from '@dealstack/frontend/investor/pages/Investment/common/ProofFileBox/ProofFileBox';
import { useJoinAndAllocationPersonalIDsController } from './useJoinAndAllocationPersonalIDsController';
import { isPersonalProofCertificationRequired } from '@dealstack/shared/lib/common/business/kycCertification';
import { CertifiableProofUpload } from '@dealstack/frontend/investor/pages/Investment/common/CertifiableProof/CertifiableProofUpload';
import { ProofCertificationStatus } from '@dealstack/frontend/investor/pages/Investment/common/CertifiableProof/ProofCertificationStatus';
import { isCertificationRequiredFor } from '@dealstack/frontend/investor/pages/Investment/common/CertifiableProof/CertifiableProof.utils';

export const JoinAndAllocationPersonalIDs = () => {
	const tInvestor = useTranslationInvestor();

	const {
		proofOfIdentityDetails,
		proofOfAddressDetails,
		onDropProofOfIdentity,
		onDeleteProofOfIdentity,
		onConfirmProofOfIdentity,
		onDropProofOfAddress,
		onDeleteProofOfAddress,
		onConfirmProofOfAddress,
		onResetAll,
		isLoading,
		refetchAll,
		uploadProofOfAddressRequest,
		uploadProofOfIdentityRequest,
		kycSetting,
	} = useJoinAndAllocationPersonalIDsController();

	const isProofOfIdentityRequired = kycSetting?.isProofOfIdentityRequired ?? true;
	const isProofOfAddressRequired = kycSetting?.isProofOfAddressRequired ?? true;

	const isCertifiedCopiesRequested = isPersonalProofCertificationRequired(kycSetting);

	const hasInheritedItems =
		proofOfIdentityDetails?.subjectAction != null ||
		proofOfAddressDetails?.subjectAction != null;

	const hasUnconfirmedInherited =
		proofOfIdentityDetails?.subjectAction === SubjectAction.NOT_YET_ACTED ||
		proofOfAddressDetails?.subjectAction === SubjectAction.NOT_YET_ACTED;
	const hasAnyFiles = !!proofOfIdentityDetails || !!proofOfAddressDetails;
	const showReset = hasAnyFiles && !hasUnconfirmedInherited;

	return (
		<Flex direction="column" gap="2rem" maw="40rem">
			{isLoading && <InlineLoader />}
			{hasInheritedItems && (
				<Flex direction="column" gap="0.25rem">
					<Text fw={700}>{tInvestor('investment.join.kyc.inherited.title')}</Text>
					<Text size="sm" c="dark.0">
						{tInvestor('investment.join.kyc.inherited.description')}
					</Text>
				</Flex>
			)}
			{isProofOfIdentityRequired && (
				<ProofWrapper>
					<Flex align="center" gap="0.5rem">
						<Text c="dark.0">{tInvestor('investment.join.kyc.proof_id')}</Text>
						{proofOfIdentityDetails?.subjectAction != null &&
							proofOfIdentityDetails?.createdAt && (
								<LastVerifiedBadge date={proofOfIdentityDetails.createdAt} />
							)}
					</Flex>
					{!!proofOfIdentityDetails && (
						<ProofFileBox
							fileName={proofOfIdentityDetails.fileName}
							fileId={proofOfIdentityDetails.fileId}
							onDelete={onDeleteProofOfIdentity}
							onConfirm={onConfirmProofOfIdentity}
							rejectionComment={proofOfIdentityDetails.rejectionComment}
							isRejected={isKycRejected(proofOfIdentityDetails.status)}
							date={proofOfIdentityDetails.createdAt}
							dataTestId={TEST_IDS.INVESTOR_KYC_ID_UPLOAD}
							subjectAction={proofOfIdentityDetails.subjectAction}
							certificationSlot={
								<ProofCertificationStatus
									kycFile={proofOfIdentityDetails}
									certificationRequired={isCertificationRequiredFor(
										kycSetting,
										KYCFileType.PROOF_OF_IDENTITY
									)}
									isRejected={isKycRejected(proofOfIdentityDetails.status)}
									onChanged={refetchAll}
								/>
							}
						/>
					)}
					{!proofOfIdentityDetails && (
						<CertifiableProofUpload
							certificationRequired={isCertificationRequiredFor(
								kycSetting,
								KYCFileType.PROOF_OF_IDENTITY
							)}
							onDropFile={onDropProofOfIdentity}
							loading={uploadProofOfIdentityRequest.isPending}
							dataTestId={TEST_IDS.INVESTOR_KYC_ID_FILE_INPUT}
						/>
					)}
				</ProofWrapper>
			)}

			{isProofOfAddressRequired && (
				<ProofWrapper>
					<Flex align="center" gap="0.5rem">
						<Text c="dark.0">{tInvestor('investment.join.kyc.proof_address')}</Text>
						{proofOfAddressDetails?.subjectAction != null &&
							proofOfAddressDetails?.createdAt && (
								<LastVerifiedBadge date={proofOfAddressDetails.createdAt} />
							)}
					</Flex>
					{!!proofOfAddressDetails && (
						<ProofFileBox
							fileName={proofOfAddressDetails.fileName}
							fileId={proofOfAddressDetails.fileId}
							onDelete={onDeleteProofOfAddress}
							onConfirm={onConfirmProofOfAddress}
							rejectionComment={proofOfAddressDetails.rejectionComment}
							isRejected={isKycRejected(proofOfAddressDetails.status)}
							date={proofOfAddressDetails.createdAt}
							dataTestId={TEST_IDS.INVESTOR_KYC_ADDRESS_UPLOAD}
							subjectAction={proofOfAddressDetails.subjectAction}
							certificationSlot={
								<ProofCertificationStatus
									kycFile={proofOfAddressDetails}
									certificationRequired={isCertificationRequiredFor(
										kycSetting,
										KYCFileType.PROOF_OF_ADDRESS
									)}
									isRejected={isKycRejected(proofOfAddressDetails.status)}
									onChanged={refetchAll}
								/>
							}
						/>
					)}
					{!proofOfAddressDetails && (
						<CertifiableProofUpload
							certificationRequired={isCertificationRequiredFor(
								kycSetting,
								KYCFileType.PROOF_OF_ADDRESS
							)}
							onDropFile={onDropProofOfAddress}
							loading={uploadProofOfAddressRequest.isPending}
							dataTestId={TEST_IDS.INVESTOR_KYC_ADDRESS_FILE_INPUT}
						/>
					)}
				</ProofWrapper>
			)}

			{showReset && (
				<Flex>
					<Button
						size="sm"
						variant="outline"
						color="dark"
						onClick={onResetAll}
						data-testid={TEST_IDS.INVESTOR_KYC_INHERITED_DISMISS_BUTTON}
					>
						{tInvestor('investment.join.kyc.inherited.reset')}
					</Button>
				</Flex>
			)}

			<KycInstructionBar isCertifiedCopiesOfDocumentsRequested={isCertifiedCopiesRequested} />
		</Flex>
	);
};

const ProofWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	padding: 0.5rem;
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[4]};
	border-radius: 4px;
`;
